import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, Check, Mic, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  classifyMicrophonePermissionFailure,
  type MicrophonePermissionFailureKind,
} from '@/modules/elevenlabs/components/microphonePermission';
import { Button } from '@/modules/ui/components/button/Button';
import { Heading } from '@/modules/ui/components/heading/Heading';

type MicrophonePermissionState =
  | { status: 'requesting' }
  | { status: 'ready'; hasHeardAudio: boolean }
  | { status: 'error'; kind: MicrophonePermissionFailureKind };

interface PrescreenMicrophonePermissionProps {
  onStart: (microphoneAccessGranted: boolean) => void;
  onError: (error: Error) => void;
}

const errorCopy: Record<
  MicrophonePermissionFailureKind,
  { title: string; message: string }
> = {
  permissionDenied: {
    title: 'Microphone access is blocked',
    message:
      'Use the microphone or site-settings icon in your browser’s address bar, choose Allow for this site, then try again.',
  },
  deviceUnavailable: {
    title: 'We couldn’t find an available microphone',
    message: 'Connect or enable a microphone, then try again.',
  },
  unknown: {
    title: 'We couldn’t enable your microphone',
    message: 'Check your browser’s microphone settings, then try again.',
  },
};

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Microphone setup failed');
}

export function PrescreenMicrophonePermission({
  onStart,
  onError,
}: PrescreenMicrophonePermissionProps) {
  const shouldReduceMotion = useReducedMotion();
  const [state, setState] = useState<MicrophonePermissionState>({
    status: 'requesting',
  });
  const [level, setLevel] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const activeTestRef = useRef(0);

  const cleanupMicrophoneTest = () => {
    activeTestRef.current += 1;
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== 'closed') {
      void audioContext.close().catch(() => undefined);
    }
  };

  useEffect(() => cleanupMicrophoneTest, []);

  const handleTestMicrophone = async () => {
    cleanupMicrophoneTest();
    const testId = activeTestRef.current;
    setLevel(0);
    setState({ status: 'requesting' });

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone access is not available in this browser.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (testId !== activeTestRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      await audioContext.resume();

      if (testId !== activeTestRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        void audioContext.close().catch(() => undefined);
        return;
      }

      setState({ status: 'ready', hasHeardAudio: false });
      const samples = new Uint8Array(analyser.fftSize);
      const readLevel = () => {
        analyser.getByteTimeDomainData(samples);
        const averageDeviation =
          samples.reduce((total, sample) => total + Math.abs(sample - 128), 0) /
          samples.length;
        const nextLevel = Math.min(100, Math.round(averageDeviation * 7));

        setLevel(nextLevel);
        if (averageDeviation > 2) {
          setState((current) =>
            current.status === 'ready' && !current.hasHeardAudio
              ? { ...current, hasHeardAudio: true }
              : current,
          );
        }
        animationFrameRef.current = window.requestAnimationFrame(readLevel);
      };
      readLevel();
    } catch (requestError) {
      if (testId !== activeTestRef.current) return;

      cleanupMicrophoneTest();
      const normalizedError = normalizeError(requestError);
      setState({
        status: 'error',
        kind: classifyMicrophonePermissionFailure(requestError),
      });
      onError(normalizedError);
    }
  };

  useEffect(() => {
    void handleTestMicrophone();
    // The microphone check deliberately begins once when this screen mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = () => {
    cleanupMicrophoneTest();
    onStart(true);
  };

  const handleStartAnyway = () => {
    cleanupMicrophoneTest();
    onStart(false);
  };

  const isRequesting = state.status === 'requesting';
  const isReady = state.status === 'ready';
  const error = state.status === 'error' ? errorCopy[state.kind] : null;
  const statusMessage = isReady
    ? state.hasHeardAudio
      ? '✅ Microphone is ready. We can hear you.'
      : 'Microphone is ready. We have not heard sound yet, but you can still start your voice intro.'
    : isRequesting
      ? 'Your browser may show a permission prompt. Choose Allow to test your microphone.'
      : '';

  return (
    <motion.section
      initial={shouldReduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={shouldReduceMotion ? undefined : { opacity: 0, y: -12 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.3, ease: 'easeOut' }}
      className="mx-auto flex w-full max-w-xl flex-col items-center text-center"
      aria-labelledby="microphone-permission-heading"
    >
      <div className="bg-secondary-150 text-primary flex size-16 items-center justify-center rounded-2xl">
        <Mic className="size-8" aria-hidden="true" />
      </div>

      <Heading
        id="microphone-permission-heading"
        variant="h2"
        className="text-foreground mt-6 text-balance"
      >
        Test your microphone
      </Heading>

      {isReady && (
        <div className="mt-7 w-full max-w-lg text-left">
          <div
            className="grid h-12 grid-cols-12 items-end gap-1"
            role="meter"
            aria-label="Microphone input level"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={level}
            aria-valuetext={
              state.hasHeardAudio ? 'Sound detected' : 'No sound detected yet'
            }
          >
            {Array.from({ length: 12 }, (_, index) => {
              const threshold = ((index + 1) / 12) * 100;
              const isActive = level >= threshold;
              return (
                <span
                  key={index}
                  className={`rounded-sm transition-colors motion-reduce:transition-none ${
                    isActive ? 'bg-primary' : 'bg-secondary-150'
                  }`}
                  style={{ height: `${36 + (index % 4) * 18}%` }}
                  aria-hidden="true"
                />
              );
            })}
          </div>
          <p
            role="status"
            aria-live="polite"
            className="text-neutral-650 mt-3 text-center text-sm leading-6"
          >
            {statusMessage}
          </p>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 mt-6 w-full rounded-xl border p-4 text-left"
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className="text-destructive mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-semibold">{error.title}</p>
              <p className="text-neutral-650 mt-1 text-sm leading-6">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {!isReady && !isRequesting && (
        <div className="mt-7 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <Button
            variant="primary"
            size="lg"
            className="h-12 min-w-56 rounded-xl font-semibold motion-reduce:transition-none"
            onClick={handleTestMicrophone}
          >
            {state.status === 'error' && (
              <RotateCcw className="size-5" aria-hidden="true" />
            )}
            Try again
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="h-12 min-w-56 rounded-xl font-semibold"
            onClick={handleStartAnyway}
          >
            Start voice intro anyway
          </Button>
        </div>
      )}

      {isRequesting && (
        <p
          role="status"
          aria-live="polite"
          className="text-neutral-650 mt-7 text-sm leading-6"
        >
          {statusMessage}
        </p>
      )}

      {isReady && (
        <Button
          variant="primary"
          size="lg"
          className="mt-7 h-12 min-w-56 rounded-xl font-semibold"
          onClick={handleStart}
        >
          <Check className="size-5" aria-hidden="true" />
          Start voice intro
        </Button>
      )}
    </motion.section>
  );
}
